import { lazy, Suspense } from 'react';
import { Navigate, useRoutes } from 'react-router-dom';
// layouts
import DashboardLayout from './layouts/dashboard';
import SimpleLayout from './layouts/simple';
//
import BlogPage from './pages/BlogPage';
import UserPage from './pages/UserPage';
import LoginPage from './pages/LoginPage';
import SessionListPage from './pages/SessionListPage';
import Page404 from './pages/Page404';
import StandardsPage from './pages/StandardsPage';
import AccountPage from './pages/AccountPage';
import DataCapturePage from './pages/DataCapturePage';
import RequestPasswordResetPage from './pages/RequestPasswordResetPage';
import PasswordResetPage from './pages/ResetPasswordPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import SensorPage from './pages/SensorPage';
import DeviceFilesPage from './pages/DeviceFilesPage';
import SensorManagerPage from './pages/SensorManagerPage';
import SensorMonitoringPage from './pages/SensorMonitoringPage';
import LiveViewerPage from './pages/LiveViewerPage';
import HomePage from './pages/HomePage';
import InstructionsPage from './pages/InstructionsPage';
import WorkspaceListPage from './pages/WorkspaceListPage';
import WorkspacePage from './pages/WorkspacePage';
import AdminUserManagementPage from './pages/AdminUserManagementPage';
import ChangePasswordPage from './pages/ChangePasswordPage';
import PlansPage from './pages/PlansPage';
import HelpPage from './pages/HelpPage';
import InviteAcceptPage from './pages/InviteAcceptPage';
import OnboardingPage from './pages/OnboardingPage';
import PrivateRoute from './PrivateRoute';
import AdminRoute from './AdminRoute';
import LoadingSpinner from './components/LoadingSpinner';

const TermsAcceptancePage = lazy(() => import('./pages/TermsAcceptancePage'));
// ----------------------------------------------------------------------

export default function Router() {
  const routes = useRoutes([
    {
      path: '/dashboard',
      element: (
        <PrivateRoute>
          <DashboardLayout />
        </PrivateRoute>
      ),
      children: [
        { element: <Navigate to="/dashboard/app" />, index: true },
        { path: 'app', element: <SessionListPage /> },
        { path: 'user', element: <UserPage /> },
        { path: 'account', element: <AccountPage /> },
        { path: 'standards', element: <StandardsPage /> },
        { path: 'blog', element: <BlogPage /> },
        { path: 'sensors', element: <SensorPage /> },
        { path: 'sessionList', element: <SessionListPage /> },
        { path: 'session/:dataCaptureId', element: <DataCapturePage /> },
        { path: 'workspaces', element: <WorkspaceListPage /> },
        { path: 'workspace/:workspaceId', element: <WorkspacePage /> },
        { path: 'sensor-manager', element: <SensorManagerPage /> },
        { path: 'monitoring', element: <SensorMonitoringPage /> },
        { path: 'live', element: <LiveViewerPage /> },
        { path: 'device-files', element: <Navigate to="/dashboard/sensor-manager" replace /> },
        { path: 'change-password', element: <ChangePasswordPage /> },
        { path: 'plans', element: <PlansPage /> },
        { path: 'help', element: <HelpPage /> },
        {
          path: 'admin/users',
          element: (
            <AdminRoute>
              <AdminUserManagementPage />
            </AdminRoute>
          ),
        },
        {
          path: 'onboarding',
          element: (
            <AdminRoute>
              <OnboardingPage />
            </AdminRoute>
          ),
        },
      ],
    },
    {
      path: '/terms',
      element: (
        <PrivateRoute>
          <Suspense fallback={<LoadingSpinner message="Loading..." />}>
            <TermsAcceptancePage />
          </Suspense>
        </PrivateRoute>
      ),
    },
    {
      index: true,
      element: <HomePage />,
    },
    {
      path: 'login',
      element: <LoginPage />,
    },
    {
      path: 'requestPasswordReset',
      element: <RequestPasswordResetPage />,
    },
    {
      path: 'passwordReset/:token',
      element: <PasswordResetPage />,
    },
    {
      path: 'invite/:token',
      element: <InviteAcceptPage />,
    },
    {
      path: 'privacypolicy',
      element: <PrivacyPolicyPage />,
    },
    {
      path: 'rawprivacypolicy',
      element: (
        <iframe src="/assets/documents/PrivacyPolicy.md" style={{ width: '100%', height: '100vh', border: 'none' }} />
      ),
    },
    {
      element: <SimpleLayout />,
      children: [
        { element: <Navigate to="/dashboard/app" />, index: true },
        { path: '404', element: <Page404 /> },
        { path: '*', element: <Navigate to="/404" /> },
        { path: 'help', element: <InstructionsPage /> },
      ],
    },
    {
      path: '*',
      element: <Navigate to="/404" replace />,
    },
  ]);

  return routes;
}
